/**
 * Componente para los botones de acción de una tarea cron
 * Ejecutar manualmente, refrescar, etc.
 */
import React from 'react'
import { ArrowPathIcon, PlayIcon } from '@heroicons/react/24/outline'

interface CronTaskActionsProps {
  onExecute: () => Promise<void>
  isLoading: boolean
  isEnabled: boolean
  buttonColor?: string // Color del botón (ej: 'bigcenter', 'mercadol', 'red-blue')
  onManualExecute?: () => void // Función para ejecutar proceso manual
  isManualLoading?: boolean // Estado de carga del botón manual
}

const CronTaskActions: React.FC<CronTaskActionsProps> = React.memo(
  ({
    onExecute,
    isLoading,
    isEnabled,
    buttonColor = 'mercadol',
    onManualExecute,
    isManualLoading = false,
  }) => {
    // Determinar clases de color según el buttonColor
    const getColorClasses = () => {
      if (isLoading || !isEnabled) {
        return 'bg-gray-100 text-gray-400 cursor-not-allowed'
      }

      // Usar clases completas para que Tailwind las detecte
      if (buttonColor === 'bigcenter') {
        return 'bg-bigcenter text-white hover:bg-bigcenter/90 active:bg-bigcenter/80 shadow-sm hover:shadow-md'
      }
      if (buttonColor === 'mercadol') {
        return 'bg-mercadol text-gray-900 hover:bg-mercadol/90 active:bg-mercadol/80 shadow-sm hover:shadow-md'
      }
      if (buttonColor === 'bold') {
        return 'bg-indigo-600 text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm hover:shadow-md'
      }
      if (buttonColor === 'colvanes') {
        return 'bg-red-colvanes text-white hover:bg-red-colvanes/90 active:bg-red-colvanes/80 shadow-sm hover:shadow-md'
      }
      // Color por defecto
      return 'bg-mercadol text-gray-900 hover:bg-mercadol/90 active:bg-mercadol/80 shadow-sm hover:shadow-md'
    }

    // Si es botón rojo-azul, usar estructura especial sin gradiente
    if (buttonColor === 'red-blue') {
      return (
        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onExecute()
            }}
            disabled={isLoading || !isEnabled}
            className={`
              flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium
              transition-all duration-200 relative overflow-hidden
              ${isLoading || !isEnabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-white shadow-sm hover:shadow-md'}
            `}
            aria-label="Ejecutar"
          >
            {/* Mitad izquierda roja (Colvanes) */}
            <div className="absolute left-0 top-0 bottom-0 w-1/2 bg-red-colvanes hover:opacity-90 active:opacity-80 transition-opacity" />
            {/* Mitad derecha azul (Envía) */}
            <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-blue-envia hover:opacity-90 active:opacity-80 transition-opacity" />
            {/* Contenido del botón */}
            <span className="relative z-10 flex items-center space-x-2">
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span>Ejecutando...</span>
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5" />
                  <span>Ejecutar</span>
                </>
              )}
            </span>
          </button>
          {/* Botón Manual si está disponible */}
          {onManualExecute && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onManualExecute()
              }}
              disabled={isManualLoading || !isEnabled}
              className={`
                flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium
                transition-all duration-200
                ${isManualLoading || !isEnabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-envia text-white hover:bg-blue-envia/90 active:bg-blue-envia/80 shadow-sm hover:shadow-md'
                }
              `}
              aria-label="Ejecutar proceso manual"
            >
              {isManualLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <PlayIcon className="h-5 w-5" />
                  <span>Manual</span>
                </>
              )}
            </button>
          )}
        </div>
      )
    }

    return (
      <div className="flex space-x-2">
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onExecute()
          }}
          disabled={isLoading || !isEnabled}
          className={`
            flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium
            transition-all duration-200
            ${getColorClasses()}
          `}
          aria-label="Ejecutar"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              <span>Ejecutando...</span>
            </>
          ) : (
            <>
              <PlayIcon className="h-5 w-5" />
              <span>Ejecutar Ahora</span>
            </>
          )}
        </button>
        {/* Botón Manual si está disponible */}
        {onManualExecute && (
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onManualExecute()
            }}
            disabled={isManualLoading || !isEnabled}
            className={`
              flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium
              transition-all duration-200
              ${isManualLoading || !isEnabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-envia text-white hover:bg-blue-envia/90 active:bg-blue-envia/80 shadow-sm hover:shadow-md'
              }
            `}
            aria-label="Ejecutar proceso manual"
          >
            {isManualLoading ? (
              <>
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <PlayIcon className="h-5 w-5" />
                <span>Manual</span>
              </>
            )}
          </button>
        )}
      </div>
    )
  }
)

CronTaskActions.displayName = 'CronTaskActions'

export default CronTaskActions

